/**
 * Shared between the web app (web_myplann) and the React Native host (app_myplann).
 * Both projects re-export this file via a relative-path shim.
 */

export type Message<
	Ttype extends keyof typeof WebViewMessageTypes,
	T,
	TMetadata = undefined,
> = { type: Ttype; payload: T } & (TMetadata extends undefined
	? { metadata?: undefined }
	: { metadata: TMetadata });

export type Language = "cs" | "en";

export const DEFAULT_LANGUAGE: Language = "cs";

export const pilotGoalStorageKey = "pilotGoal";

export interface PilotRights {
	pilotGoal: boolean;
	pilotNotificationCenter: boolean;
	pilotMyplannPortfolio: boolean;
	pilotMyplannReminders: boolean;
	pilotMyplannIdentificationDocuments?: boolean;
}

export const NO_PILOT_RIGHTS: PilotRights = {
	pilotGoal: false,
	pilotNotificationCenter: false,
	pilotMyplannPortfolio: false,
	pilotMyplannReminders: false,
	pilotMyplannIdentificationDocuments: false,
};

export const themeStorageKey = "prefers-color-scheme";

export const WebViewMessageTypes = {
	INIT_REQUEST: "INIT_REQUEST",
	INIT_RESPONSE: "INIT_RESPONSE",
	SWITCH_LANGUAGE: "SWITCH_LANGUAGE",
	SWITCH_DARK_MODE: "SWITCH_DARK_MODE",
	NAVIGATION_REQUEST: "NAVIGATION_REQUEST",
	NAVIGATION_RESPONSE: "NAVIGATION_RESPONSE",
	NAVIGATE_BACK_REQUEST: "NAVIGATE_BACK_REQUEST",
	NAVIGATE_BACK_RESPONSE: "NAVIGATE_BACK_RESPONSE",
	NAVIGATE_CHAT_REQUEST: "NAVIGATE_CHAT_REQUEST",
	NAVIGATE_CHAT_RESPONSE: "NAVIGATE_CHAT_RESPONSE",
	NAVIGATE_NOTIFICATION_REQUEST: "NAVIGATE_NOTIFICATION_REQUEST",
	NAVIGATE_NOTIFICATION_RESPONSE: "NAVIGATE_NOTIFICATION_RESPONSE",
	PICK_ATTACHMENT_PHOTO_REQUEST: "PICK_ATTACHMENT_PHOTO_REQUEST",
	PICK_ATTACHMENT_PHOTO_RESPONSE: "PICK_ATTACHMENT_PHOTO_RESPONSE",
	PICK_ATTACHMENT_GALLERY_REQUEST: "PICK_ATTACHMENT_GALLERY_REQUEST",
	PICK_ATTACHMENT_GALLERY_RESPONSE: "PICK_ATTACHMENT_GALLERY_RESPONSE",
	PICK_ATTACHMENT_FILE_REQUEST: "PICK_ATTACHMENT_FILE_REQUEST",
	PICK_ATTACHMENT_FILE_RESPONSE: "PICK_ATTACHMENT_FILE_RESPONSE",
	SAVE_ATTACHMENT_REQUEST: "SAVE_ATTACHMENT_REQUEST",
	SAVE_ATTACHMENT_RESPONSE: "SAVE_ATTACHMENT_RESPONSE",
	REMOVE_ATTACHMENT_REQUEST: "REMOVE_ATTACHMENT_REQUEST",
	REMOVE_ATTACHMENT_RESPONSE: "REMOVE_ATTACHMENT_RESPONSE",
	HANDLE_DOCUMENT_REQUEST: "HANDLE_DOCUMENT_REQUEST",
	HANDLE_DOCUMENT_RESPONSE: "HANDLE_DOCUMENT_RESPONSE",
	CONSOLE_LOG_REQUEST: "CONSOLE_LOG_REQUEST",
	CONSOLE_LOG_RESPONSE: "CONSOLE_LOG_RESPONSE",
	REFRESH_NOTIFICATIONS_REQUEST: "REFRESH_NOTIFICATIONS_REQUEST",
	REFRESH_APP_INFO_REQUEST: "REFRESH_APP_INFO_REQUEST",
	BIOMETRY_STATUS_REQUEST: "BIOMETRY_STATUS_REQUEST",
	BIOMETRY_STATUS_RESPONSE: "BIOMETRY_STATUS_RESPONSE",
	LOG_OUT_REQUEST: "LOG_OUT_REQUEST",
	LOG_OUT_RESPONSE: "LOG_OUT_RESPONSE",
	SESSION_EXPIRED_REQUEST: "SESSION_EXPIRED_REQUEST",
	SESSION_EXPIRED_RESPONSE: "SESSION_EXPIRED_RESPONSE",
	CHANGE_PROFILE_IMAGE_REQUEST: "CHANGE_PROFILE_IMAGE_REQUEST",
	CHANGE_PROFILE_IMAGE_RESPONSE: "CHANGE_PROFILE_IMAGE_RESPONSE",
	REMOVE_USER_REQUEST: "REMOVE_USER_REQUEST",
	REMOVE_USER_RESPONSE: "REMOVE_USER_RESPONSE",
	CLEAR_CACHE_REQUEST: "CLEAR_CACHE_REQUEST",
	CHANGE_DASHBOARD_COLOR_REQUEST: "CHANGE_DASHBOARD_COLOR_REQUEST",
	CHANGE_DASHBOARD_COLOR_RESPONSE: "CHANGE_DASHBOARD_COLOR_RESPONSE",
	OPEN_EXTERNAL_URL_REQUEST: "OPEN_EXTERNAL_URL_REQUEST",
	OPEN_EXTERNAL_URL_RESPONSE: "OPEN_EXTERNAL_URL_RESPONSE",
	SHOW_NAVBAR_REQUEST: "SHOW_NAVBAR_REQUEST",
	SHOW_NAVBAR_RESPONSE: "SHOW_NAVBAR_RESPONSE",
	PROCESSING_OVERLAY_REQUEST: "PROCESSING_OVERLAY_REQUEST",
	PROCESSING_OVERLAY_RESPONSE: "PROCESSING_OVERLAY_RESPONSE",
	CONNECTIVITY_STATUS: "CONNECTIVITY_STATUS",
	BRIDGE_READY: "BRIDGE_READY",
	SET_STATUS_BAR_OVERLAY_REQUEST: "SET_STATUS_BAR_OVERLAY_REQUEST",
	SET_STATUS_BAR_OVERLAY_RESPONSE: "SET_STATUS_BAR_OVERLAY_RESPONSE",
	SET_ACTIVE_TAB_REQUEST: "SET_ACTIVE_TAB_REQUEST",
	SET_ACTIVE_TAB_RESPONSE: "SET_ACTIVE_TAB_RESPONSE",
	SET_TAB_BAR_COLLAPSED_REQUEST: "SET_TAB_BAR_COLLAPSED_REQUEST",
	SET_TAB_BAR_COLLAPSED_RESPONSE: "SET_TAB_BAR_COLLAPSED_RESPONSE",
	TAB_BAR_COLLAPSED_STATUS: "TAB_BAR_COLLAPSED_STATUS",
	SET_CHAT_UNREAD_COUNT_REQUEST: "SET_CHAT_UNREAD_COUNT_REQUEST",
	SET_CHAT_UNREAD_COUNT_RESPONSE: "SET_CHAT_UNREAD_COUNT_RESPONSE",
	ALERT_BOX_ACTION_PERFORMED_REQUEST: "ALERT_BOX_ACTION_PERFORMED_REQUEST",
	SET_ADVISORS_PENDING_COUNT_REQUEST: "SET_ADVISORS_PENDING_COUNT_REQUEST",
	SET_ADVISORS_PENDING_COUNT_RESPONSE: "SET_ADVISORS_PENDING_COUNT_RESPONSE",
} as const;

export const NavigationTypes = {
	DocumentListScreen: "DocumentListScreen",
	ProfileScreen: "ProfileScreen",
	ChatScreen: "ChatScreen",
	MortgageCalculatorScreen: "MortgageCalculatorScreen",
	TravelInsuranceCalculatorScreen: "TravelInsuranceCalculatorScreen",
	PropertyInsuranceCalculatorScreen: "PropertyInsuranceCalculatorScreen",
	VehicleInsuranceCalculatorScreen: "VehicleInsuranceCalculatorScreen",
	ConsumerLoanCalculatorScreen: "ConsumerLoanCalculatorScreen",
	PensionAnnuityCalculatorScreen: "PensionAnnuityCalculatorScreen",
	RefinancingCalculatorScreen: "RefinancingCalculatorScreen",
	CalculatorListScreen: "CalculatorListScreen",
	WebDashboard: "WebDashboard",
	WebPortfolio: "WebPortfolio",
	WebActivity: "WebActivity",
	WebAdvisor: "WebAdvisor",
	ZZJ: "ZZJ",
	GoalsScreen: "GoalsScreen",
	MoreScreen: "MoreScreen",
} as const;

export type NavigationType = keyof typeof NavigationTypes;

export const HandleDocumentTypes = {
	OPEN: "OPEN",
	SHARE: "SHARE",
} as const;

export const NavigationDestinationType = {
	task: "task",
	taskTerm: "taskTerm",
	taskTermNotice: "taskTermNotice",
	activity: "activity",
	activityTerm: "activityTerm",
	activityTermNotice: "activityTermNotice",
	chat: "chat",
	investmentQuestionnaire: "investmentQuestionnaire",
	reminder: "reminder",
	portfolio: "portfolio",
	identificationDocument: "identificationDocument",
} as const;

export type NavigationDestination = keyof typeof NavigationDestinationType;

const NAVIGATION_DESTINATIONS = Object.keys(
	NavigationDestinationType,
) as NavigationDestination[];

export const normalizeNavigationDestination = (
	value: unknown,
): NavigationDestination | undefined => {
	if (typeof value !== "string") {
		return undefined;
	}
	const needle = value.trim().toLowerCase();
	return NAVIGATION_DESTINATIONS.find((key) => key.toLowerCase() === needle);
};

type EmptyPayload = Record<string, never>;

export interface File {
	filename: string;
	base64: string;
}

export const EMPTY_FILE: File = { filename: "", base64: "" };

export type InitRequest = Message<"INIT_REQUEST", EmptyPayload>;

export type InitResponse = Message<
	"INIT_RESPONSE",
	{
		bearer: string;
		tokenVerify: string;
		language: Language;
		darkMode: boolean;
		appVersion: string;
		isOnline: boolean;
		biometryEnabled?: boolean;
		rights: PilotRights;
		supportedNavigationTypes?: NavigationType[];
	}
>;

export type ConnectivityStatus = Message<
	"CONNECTIVITY_STATUS",
	{ isOnline: boolean }
>;

export type SwitchLanguage = Message<"SWITCH_LANGUAGE", { language: Language }>;

export type SwitchDarkMode = Message<"SWITCH_DARK_MODE", { darkMode: boolean }>;

export type CalculatorPrefill = {
	propertyPrice?: number;
	originMonthlyPayment?: number;
	originInterest?: number;
	originMortgageYears?: number;
	repaymentPeriod?: number;
	contractId?: number;
	contractNumber?: string;
	producer?: string;
};

export type NavigationRequest = Message<
	"NAVIGATION_REQUEST",
	{
		url?: string;
		type?: NavigationType;
		calculatorPrefill?: CalculatorPrefill;
	}
>;

export type NavigationResponse = Message<
	"NAVIGATION_RESPONSE",
	{ navigated: boolean }
>;

export type NavigateBackRequest = Message<
	"NAVIGATE_BACK_REQUEST",
	{ navigateBack: boolean }
>;

export type NavigateBackResponse = Message<
	"NAVIGATE_BACK_RESPONSE",
	{ navigated: boolean }
>;

export type NavigateChatRequest = Message<
	"NAVIGATE_CHAT_REQUEST",
	{
		conversationId: string;
		title: string;
		advisorId?: string;
		message?: string;
	}
>;

export type NavigateChatResponse = Message<
	"NAVIGATE_CHAT_RESPONSE",
	{ navigated: boolean }
>;

export const parseNotificationId = (value: unknown) => {
	if (typeof value !== "string" && typeof value !== "number") {
		return undefined;
	}
	const parsed = Number(value);
	return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const parseNotificationIds = (value: unknown): number[] | undefined => {
	let items: unknown[];
	if (typeof value === "string") {
		items = value.split(",");
	} else if (Array.isArray(value)) {
		items = value;
	} else {
		return undefined;
	}
	// No spread / for-of here: this file is shared with the RN shell, whose Babel
	// preset would rewrite them into @babel/runtime helpers that jest cannot
	// resolve from outside app_myplann/.
	const parsed: number[] = [];
	items.forEach((item) => {
		const id = parseNotificationId(
			typeof item === "string" ? item.trim() || undefined : item,
		);
		if (id !== undefined && !parsed.includes(id)) {
			parsed.push(id);
		}
	});
	return parsed.length > 0 ? parsed : undefined;
};

export type NavigateNotificationRequest = Message<
	"NAVIGATE_NOTIFICATION_REQUEST",
	{
		type?: string;
		sourceId?: string;
		portfolioId?: string;
		id?: number;
		ids?: number[];
		clientType?: string;
		identificationNumber?: string;
		notificationType?: string;
		eventType?: string;
		eventDate?: string;
		entityType?: string;
		entityId?: string;
		reminderId?: string;
	}
>;

export type NavigateNotificationResponse = Message<
	"NAVIGATE_NOTIFICATION_RESPONSE",
	{ received: boolean }
>;

export const AttachmentPickStatuses = {
	picked: "picked",
	cancelled: "cancelled",
	error: "error",
} as const;

export type AttachmentPickStatus =
	(typeof AttachmentPickStatuses)[keyof typeof AttachmentPickStatuses];

export type AttachmentPickPayload = {
	file: File;
	status?: AttachmentPickStatus;
};

export type PickAttachmentPhotoRequest = Message<
	"PICK_ATTACHMENT_PHOTO_REQUEST",
	EmptyPayload
>;

export type PickAttachmentPhotoResponse = Message<
	"PICK_ATTACHMENT_PHOTO_RESPONSE",
	AttachmentPickPayload
>;

export type PickAttachmentGalleryRequest = Message<
	"PICK_ATTACHMENT_GALLERY_REQUEST",
	EmptyPayload
>;

export type PickAttachmentGalleryResponse = Message<
	"PICK_ATTACHMENT_GALLERY_RESPONSE",
	AttachmentPickPayload
>;

export type PickAttachmentFileRequest = Message<
	"PICK_ATTACHMENT_FILE_REQUEST",
	EmptyPayload
>;

export type PickAttachmentFileResponse = Message<
	"PICK_ATTACHMENT_FILE_RESPONSE",
	AttachmentPickPayload
>;

export type PickAttachmentResponse =
	| PickAttachmentPhotoResponse
	| PickAttachmentGalleryResponse
	| PickAttachmentFileResponse;

export type SaveAttachmentRequest = Message<
	"SAVE_ATTACHMENT_REQUEST",
	{ file: File }
>;

export type SaveAttachmentResponse = Message<
	"SAVE_ATTACHMENT_RESPONSE",
	{ success: boolean; downloaded: string[] }
>;

export type RemoveAttachmentRequest = Message<
	"REMOVE_ATTACHMENT_REQUEST",
	{ filename: string }
>;

export type RemoveAttachmentResponse = Message<
	"REMOVE_ATTACHMENT_RESPONSE",
	{ success: boolean; downloaded: string[] }
>;

export type HandleDocumentPayload = {
	file: {
		base64: string;
		filename: string;
	};
	mimeType?: string;
	actionType: keyof typeof HandleDocumentTypes;
};

export type HandleDocumentRequest = Message<
	"HANDLE_DOCUMENT_REQUEST",
	HandleDocumentPayload
>;

export type HandleDocumentResponse = Message<
	"HANDLE_DOCUMENT_RESPONSE",
	{ success: boolean }
>;

export type ConsoleLogRequest = Message<
	"CONSOLE_LOG_REQUEST",
	{ message: Record<string, unknown> }
>;

export type ConsoleLogResponse = Message<
	"CONSOLE_LOG_RESPONSE",
	{ success: boolean }
>;

export type SetChatUnreadCountRequest = Message<
	"SET_CHAT_UNREAD_COUNT_REQUEST",
	{ count: number }
>;

export type SetChatUnreadCountResponse = Message<
	"SET_CHAT_UNREAD_COUNT_RESPONSE",
	{ count: number }
>;

export type BridgeAlertBoxAction = "CompareRates";

export type BridgeAlertBoxActionState = "Sent";

export type AlertBoxActionPerformedRequest = Message<
	"ALERT_BOX_ACTION_PERFORMED_REQUEST",
	{
		contractId: number;
		action: BridgeAlertBoxAction;
		state: BridgeAlertBoxActionState;
	}
>;

export type SetAdvisorsPendingCountRequest = Message<
	"SET_ADVISORS_PENDING_COUNT_REQUEST",
	{ count: number }
>;

export type SetAdvisorsPendingCountResponse = Message<
	"SET_ADVISORS_PENDING_COUNT_RESPONSE",
	{ count: number }
>;

export type RefreshNotificationsRequest = Message<
	"REFRESH_NOTIFICATIONS_REQUEST",
	EmptyPayload
>;

export type RefreshAppInfoRequest = Message<
	"REFRESH_APP_INFO_REQUEST",
	EmptyPayload
>;

export type BiometryStatusRequest = Message<
	"BIOMETRY_STATUS_REQUEST",
	{ isEnabled: boolean }
>;

export type BiometryStatusResponse = Message<
	"BIOMETRY_STATUS_RESPONSE",
	{ success: boolean; error?: string }
>;

export type LogOutRequest = Message<"LOG_OUT_REQUEST", EmptyPayload>;

export type LogOutResponse = Message<"LOG_OUT_RESPONSE", { success: boolean }>;

export type SessionExpiredRequest = Message<
	"SESSION_EXPIRED_REQUEST",
	EmptyPayload
>;

export type SessionExpiredResponse = Message<
	"SESSION_EXPIRED_RESPONSE",
	{ received: boolean }
>;

export type ChangeProfileImageRequest = Message<
	"CHANGE_PROFILE_IMAGE_REQUEST",
	EmptyPayload
>;

export type ChangeProfileImageResponse = Message<
	"CHANGE_PROFILE_IMAGE_RESPONSE",
	{ changed: boolean }
>;

export type RemoveUserRequest = Message<"REMOVE_USER_REQUEST", EmptyPayload>;

export type RemoveUserResponse = Message<
	"REMOVE_USER_RESPONSE",
	{ removed: boolean }
>;

export type ClearCacheRequest = Message<"CLEAR_CACHE_REQUEST", EmptyPayload>;

/**
 * Cadence of the dashboard tab transition: the web hero gradient cross-fade and
 * the native status bar strip driven by CHANGE_DASHBOARD_COLOR animate at this
 * duration so they stay in step.
 */
export const DASHBOARD_TRANSITION_DURATION_MS = 300;

export type ChangeDashboardColorRequest = Message<
	"CHANGE_DASHBOARD_COLOR_REQUEST",
	{ color?: string }
>;

export type ChangeDashboardColorResponse = Message<
	"CHANGE_DASHBOARD_COLOR_RESPONSE",
	{ changed: boolean }
>;

export type OpenExternalUrlCookie = {
	name: string;
	value: string;
	domain: string;
	path?: string;
	secure?: boolean;
};

export type OpenExternalUrlRequest = Message<
	"OPEN_EXTERNAL_URL_REQUEST",
	{ url: string; cookies?: OpenExternalUrlCookie[] }
>;

export type OpenExternalUrlResponse = Message<
	"OPEN_EXTERNAL_URL_RESPONSE",
	{ success: boolean }
>;

export type ShowNavbarRequest = Message<
	"SHOW_NAVBAR_REQUEST",
	{ show: boolean }
>;

export type ShowNavbarResponse = Message<
	"SHOW_NAVBAR_RESPONSE",
	{ shown: boolean }
>;

export type ProcessingOverlayRequest = Message<
	"PROCESSING_OVERLAY_REQUEST",
	{ show: boolean }
>;

export type ProcessingOverlayResponse = Message<
	"PROCESSING_OVERLAY_RESPONSE",
	{ shown: boolean }
>;

export type BridgeReady = Message<"BRIDGE_READY", EmptyPayload>;

/**
 * When enabled, the native shell drops the top safe-area inset from the WebView,
 * makes the Android status bar translucent, and exposes `--mobile-safe-area-top`
 * as a CSS var so the web can paint under the status bar. Reset on unmount.
 */
export type SetStatusBarOverlayRequest = Message<
	"SET_STATUS_BAR_OVERLAY_REQUEST",
	{ enabled: boolean; darkContent?: boolean }
>;

export type SetStatusBarOverlayResponse = Message<
	"SET_STATUS_BAR_OVERLAY_RESPONSE",
	{ enabled: boolean; safeAreaTop: number }
>;

export type PilotTab =
	| "WebDashboard"
	| "WebAdvisor"
	| "WebPortfolio"
	| "GoalsScreen"
	| "MoreScreen";

export type SetActiveTabRequest = Message<
	"SET_ACTIVE_TAB_REQUEST",
	{ tab: PilotTab }
>;

export type SetActiveTabResponse = Message<
	"SET_ACTIVE_TAB_RESPONSE",
	{ tab: PilotTab }
>;

export type SetTabBarCollapsedRequest = Message<
	"SET_TAB_BAR_COLLAPSED_REQUEST",
	{ collapsed: boolean }
>;

export type SetTabBarCollapsedResponse = Message<
	"SET_TAB_BAR_COLLAPSED_RESPONSE",
	{ collapsed: boolean }
>;

export type TabBarCollapsedStatus = Message<
	"TAB_BAR_COLLAPSED_STATUS",
	{ collapsed: boolean }
>;

export type WebViewMessage =
	| InitRequest
	| InitResponse
	| BridgeReady
	| SwitchLanguage
	| SwitchDarkMode
	| NavigationRequest
	| NavigationResponse
	| NavigateBackRequest
	| NavigateBackResponse
	| NavigateChatRequest
	| NavigateChatResponse
	| NavigateNotificationRequest
	| NavigateNotificationResponse
	| PickAttachmentPhotoRequest
	| PickAttachmentPhotoResponse
	| PickAttachmentGalleryRequest
	| PickAttachmentGalleryResponse
	| PickAttachmentFileRequest
	| PickAttachmentFileResponse
	| SaveAttachmentRequest
	| SaveAttachmentResponse
	| RemoveAttachmentRequest
	| RemoveAttachmentResponse
	| HandleDocumentRequest
	| HandleDocumentResponse
	| ConsoleLogRequest
	| ConsoleLogResponse
	| RefreshNotificationsRequest
	| RefreshAppInfoRequest
	| BiometryStatusRequest
	| BiometryStatusResponse
	| LogOutRequest
	| LogOutResponse
	| SessionExpiredRequest
	| SessionExpiredResponse
	| ChangeProfileImageRequest
	| ChangeProfileImageResponse
	| RemoveUserRequest
	| RemoveUserResponse
	| ClearCacheRequest
	| ChangeDashboardColorRequest
	| ChangeDashboardColorResponse
	| OpenExternalUrlRequest
	| OpenExternalUrlResponse
	| ShowNavbarRequest
	| ShowNavbarResponse
	| ProcessingOverlayRequest
	| ProcessingOverlayResponse
	| ConnectivityStatus
	| SetStatusBarOverlayRequest
	| SetStatusBarOverlayResponse
	| SetActiveTabRequest
	| SetActiveTabResponse
	| SetTabBarCollapsedRequest
	| SetTabBarCollapsedResponse
	| TabBarCollapsedStatus
	| SetChatUnreadCountRequest
	| SetChatUnreadCountResponse
	| AlertBoxActionPerformedRequest
	| SetAdvisorsPendingCountRequest
	| SetAdvisorsPendingCountResponse;

export type RpcMessage<T extends WebViewMessage> = T & {
	messageId: string;
};
